/**
 * @file: feed-threads.tsx
 *
 * Inbox wrapper around the feed thread list. Owns the navigation-aware bits
 * the list view delegates: "where does a tapped feed thread go" (drive the
 * shared `activeThread$` selection in the desktop master-detail, else push the
 * stack screen on mobile) and "is the list the focused surface" (so it can
 * refresh on re-focus). The list view itself stays navigation-agnostic.
 */

import { FeedThreadListView } from '@euler/app/flows/feed/pages/thread-list/components/FeedThreadListView';
import { feedThreadTitle } from '@euler/app/flows/feed/utils/feedThreadTitle';
import { FeedThreadInboxItem } from '@euler/model/feed';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { useIsFocused } from '@react-navigation/native';
import { FC, memo, useCallback } from 'react';
import { useInboxContext } from '../../context';
import { useInboxNavigation } from '../../functions/useInboxNavigation';

export const FeedThreads: FC = memo(() => {
  const { activeThread$, activeTab$, layoutMode$, feedListReloadTick$ } =
    useInboxContext();
  const [activeThread] = useBehaviorSubject(activeThread$);
  const [activeTab] = useBehaviorSubject(activeTab$);
  const [layoutMode] = useBehaviorSubject(layoutMode$);
  const [reloadTick] = useBehaviorSubject(feedListReloadTick$);
  const navigation = useInboxNavigation();

  // The list is the focused surface when the inbox screen is focused AND the
  // notifications tab is the visible one (on native both tabs stay mounted in
  // the pager, so tab visibility — not mount — is what changes here).
  const screenFocused = useIsFocused();
  const focused = screenFocused && activeTab === 'notifications';

  const onOpenThread = useCallback(
    (thread: FeedThreadInboxItem) => {
      if (layoutMode === 'desktop') {
        activeThread$.next({ type: 'feed-thread', thread });
        return;
      }
      navigation.navigate('_feedThread', {
        threadId: thread.id,
        scope: thread.scope,
        scopeId: thread.scopeId,
        title: feedThreadTitle(thread),
      });
    },
    [activeThread$, layoutMode, navigation],
  );

  const isActive = useCallback(
    (t: FeedThreadInboxItem) =>
      activeThread?.type === 'feed-thread' && activeThread.thread.id === t.id,
    [activeThread],
  );

  return (
    <FeedThreadListView
      isActive={isActive}
      focused={focused}
      reloadTick={reloadTick}
      onOpenThread={onOpenThread}
    />
  );
});
